import type { QueuedMutation } from "../../../shared/src";

const DB_NAME = "gym-tracker-offline";
const DB_VERSION = 1;
const STORE = "mutations";

export interface QueuedEntry {
  id: number;
  mutation: QueuedMutation;
  failed_reason: string | null;
  enqueued_at: string;
}

type Listener = (depth: number) => void;
const listeners = new Set<Listener>();
let cachedDepth: number | null = null;

function notify(depth: number): void {
  cachedDepth = depth;
  for (const l of listeners) l(depth);
}

export function subscribeQueueDepth(listener: Listener): () => void {
  listeners.add(listener);
  if (cachedDepth !== null) {
    listener(cachedDepth);
  } else {
    queueDepth().then(notify).catch(() => {
      // IDB unavailable (server-side render, hostile browsers). Treat as
      // empty queue so the UI keeps working without offline support.
      notify(0);
    });
  }
  return () => listeners.delete(listener);
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    if (typeof indexedDB === "undefined") {
      return Promise.reject(
        new Error("IndexedDB unavailable in this environment"),
      );
    }
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function tx(
  mode: IDBTransactionMode,
): Promise<IDBObjectStore> {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function awaitRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(mutation: QueuedMutation): Promise<QueuedEntry> {
  const store = await tx("readwrite");
  const record: Omit<QueuedEntry, "id"> = {
    mutation,
    failed_reason: null,
    enqueued_at: new Date().toISOString(),
  };
  const key = (await awaitRequest(store.add(record))) as IDBValidKey;
  const id = Number(key);
  const entry: QueuedEntry = { id, ...record };
  notify(await queueDepth());
  return entry;
}

export async function peekAll(): Promise<QueuedEntry[]> {
  const store = await tx("readonly");
  const all = (await awaitRequest(store.getAll())) as QueuedEntry[];
  return all.sort((a, b) => a.id - b.id);
}

export async function markFailed(id: number, reason: string): Promise<void> {
  const store = await tx("readwrite");
  const existing = (await awaitRequest(store.get(id))) as
    | QueuedEntry
    | undefined;
  if (!existing) return;
  existing.failed_reason = reason;
  await awaitRequest(store.put(existing));
  notify(await queueDepth());
}

export async function removeSucceeded(id: number): Promise<void> {
  const store = await tx("readwrite");
  await awaitRequest(store.delete(id));
  notify(await queueDepth());
}

export async function queueDepth(): Promise<number> {
  const store = await tx("readonly");
  return await awaitRequest(store.count());
}

export async function _clearForTests(): Promise<void> {
  const store = await tx("readwrite");
  await awaitRequest(store.clear());
  notify(0);
}

export function _resetForTests(): void {
  dbPromise = null;
  cachedDepth = null;
  listeners.clear();
}
